import type { FC } from "react";
import React, { useMemo } from "react";

import cn from "classnames";

import { calcQuantity } from "./utils";
import { Text } from "../../Text";
import { TEXT_MIXIN_BY_INDICATOR_COUNTER_SIZE } from "../constants/sizes";
import { IndicatorCore } from "../helper/IndicatorCore";

import type { IndicatorCounterProps } from "./types";

import classes from "./IndicatorCounter.styl";

export const INDICATOR_COUNTER_DISPLAY_NAME = "IndicatorCounter";
export const DEFAULT_MAX_QUANTITY = 100;
export const DEFAULT_QUANTITY = 0;

/**
 * Базовый компонент, фокусирующий на себе внимание пользователя и информирующий о количестве новой/непросмотренной информации.
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=5616-171530&t=ppTs777YfI1tVhqR-1" target="_blank">Спека</a>
 */
export const IndicatorCounter: FC<IndicatorCounterProps> = ({
    quantity = DEFAULT_QUANTITY,
    maxQuantity = DEFAULT_MAX_QUANTITY,
    "data-tsid": tsid = INDICATOR_COUNTER_DISPLAY_NAME,
    size = "24",
    ...restProps
}) => {
    const currentQuantity = useMemo(
        () => calcQuantity(quantity, maxQuantity),
        [quantity, maxQuantity],
    );

    const needsPadding = currentQuantity.length > 1;

    const wrapperClasses = cn(classes["wrapper"], {
        [classes[`__size-${size}`]]: size,
        [classes["__with-padding"]]: needsPadding,
    });

    return (
        <IndicatorCore
            {...restProps}
            className={wrapperClasses}
            data-tsid={tsid}
            data-uikit={INDICATOR_COUNTER_DISPLAY_NAME}
        >
            <div className={classes["counter"]}>
                <Text
                    typographyStyle={TEXT_MIXIN_BY_INDICATOR_COUNTER_SIZE[size]}
                    maxLines={1}
                    customVarsStyles={classes["text-custom-vars"]}
                >
                    {currentQuantity}
                </Text>
            </div>
        </IndicatorCore>
    );
};

IndicatorCounter.displayName = INDICATOR_COUNTER_DISPLAY_NAME;
